import { ClubPage } from "components/ClubPage"
import { useConnectedUser } from "components/ConnectedUserProvider"
import { Tab, TabList, TabPanel, Tabs } from "components/Tab"
import { Text } from "components/Text"
import { useTranslator } from "components/Translator"
import { Wrapper } from "components/Wrapper"
import * as PlanScope from "enums/PlanScope"
import * as Civility from "enums/Civility"
import cx from "classnames"
import * as React from "react"
import { Modal, ModalContent, ModalActions } from "components/Modal"
import { Button } from "components/Button"
import { useRouter } from "components/Router"
import { useAsync } from "@react-hook/async"
import { Alerter } from "components/Alert"
import { GiftCard } from "domains/gift/GiftCard"
import { Pressable } from "components/Pressable"
import { PlanCard } from "domains/plan/PlanCard"

export function ClubPlanIndexPage({ plans, godfather }) {
  const translator = useTranslator()
  const connectedUser = useConnectedUser()
  const router = useRouter()

  const [selectedPlan, setSelectedPlan] = React.useState(null)
  const [gifts, setGifts] = React.useState([])
  const [selectedGift, setSelectedGift] = React.useState(null)

  const userPlans = plans.filter(
    (plan) => plan.scope.enum_value === PlanScope.USER,
  )

  const organizationPlans = plans.filter(
    (plan) => plan.scope.enum_value === PlanScope.ORGANIZATION,
  )

  const isSelectedPlan = (plan) => plan === selectedPlan

  const [addPlanRequest, addPlanToCart] = useAsync(async (plan) => {
    try {
      const url = router.generate("app_user_order_cart_plan_add", {
        planId: plan.id,
        planPriceId: plan.prices[0].id,
      })
      const response = await fetch(url, {
        headers: { "X-Requested-With": "XMLHttpRequest" },
      })
      const responseData = await response.json()

      if (response.ok) {
        if (plan.gifts.length === 0) {
          if (responseData.message) {
            Alerter.success(responseData.message)
          }

          goToCart()
        } else {
          setGifts(plan.gifts)
        }
      } else {
        if (responseData.message) {
          Alerter.error(responseData.message)
        }
      }
    } catch (err) {
      console.log(err)
      Alerter.error(
        translator.trans("club.plan.index.addToCartError", null, "pages"),
      )
    }
  })

  const [addGiftRequest, addGiftToCart] = useAsync(async (gift) => {
    try {
      const url = router.generate("app_user_order_cart_gift_add", {
        giftId: gift.id,
      })
      const response = await fetch(url, {
        headers: { "X-Requested-With": "XMLHttpRequest" },
      })
      const responseData = await response.json()

      if (response.ok) {
        if (responseData.message) {
          Alerter.success(responseData.message)
        }

        setGifts([])
        setSelectedGift(null)

        goToCart()
      } else {
        if (responseData.message) {
          Alerter.error(responseData.message)
        }
      }
    } catch (err) {
      console.log(err)
      Alerter.error(
        translator.trans("club.plan.index.addToCartError", null, "pages"),
      )
    }
  })

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan)
    addPlanToCart(plan)
  }

  const goToCart = () => {
    router.redirect({ name: "app_user_order_cart_index" })
  }

  const [currentPanel, setCurrentPanel] = React.useState("userPlans")

  return (
    <ClubPage title={translator.trans("club.plan.index.title", null, "pages")}>
      <Wrapper>
        {connectedUser ? (
          <PlansList
            plans={
              connectedUser.civility.enum_value === Civility.ORGANIZATION
                ? organizationPlans
                : userPlans
            }
            onSelectPlan={handleSelectPlan}
            isSelectedPlan={isSelectedPlan}
            godfather={godfather}
            status={addPlanRequest.status}
          />
        ) : (
          <Tabs currentPanel={currentPanel} onChange={setCurrentPanel}>
            <TabList>
              <Tab
                label={translator.trans(
                  "club.plan.index.userPlans.tabLabel",
                  null,
                  "pages",
                )}
                id={"userPlans"}
              />
              <Tab
                label={translator.trans(
                  "club.plan.index.organizationPlans.tabLabel",
                  null,
                  "pages",
                )}
                id={"organizationsPlans"}
              />
            </TabList>
            <TabPanel id={"userPlans"}>
              <PlansList
                plans={userPlans}
                isSelectedPlan={isSelectedPlan}
                godfather={godfather}
              />
            </TabPanel>
            <TabPanel id={"organizationsPlans"}>
              <PlansList
                plans={organizationPlans}
                isSelectedPlan={isSelectedPlan}
                godfather={godfather}
              />
            </TabPanel>
          </Tabs>
        )}
        <Modal
          isOpen={gifts.length > 0}
          onRequestClose={goToCart}
          title={translator.trans(
            "club.plan.index.giftsModal.title",
            null,
            "pages",
          )}
        >
          <ModalContent>
            <div className="space-y-8">
              <Text tag="p">
                {translator.trans("club.plan.index.giftsModal.description")}
              </Text>
              <ul className="grid gap-2 grid-cols-2 md:grid-cols-3 lg:grid-cols-2 lg:gap-4 2xl:grid-cols-4">
                {gifts.map((gift) => {
                  return (
                    <li key={gift.id}>
                      <Pressable
                        onClick={() => setSelectedGift(gift)}
                        className={cx(
                          "rounded-lg focus:outline-hidden focus:ring-2 focus:ring-primary-light",
                          {
                            "ring-2 ring-primary-default":
                              selectedGift?.id === gift.id,
                          },
                        )}
                      >
                        <GiftCard gift={gift} />
                      </Pressable>
                    </li>
                  )
                })}
              </ul>
            </div>
          </ModalContent>
          <ModalActions>
            <Button
              color="secondary"
              busy={addGiftRequest.status === "loading"}
              disabled={!selectedGift || addGiftRequest.status === "loading"}
              onClick={() => addGiftToCart(selectedGift)}
            >
              {translator.trans("club.plan.index.select")}
            </Button>
            <Button
              variant="text"
              color="secondary"
              onClick={goToCart}
              disabled={addGiftRequest.status === "loading"}
            >
              {translator.trans(
                "club.plan.index.giftsModal.cancel",
                null,
                "pages",
              )}
            </Button>
          </ModalActions>
        </Modal>
      </Wrapper>
    </ClubPage>
  )
}

const PlansList = ({
  plans,
  onSelectPlan,
  isSelectedPlan,
  godfather,
  status,
}) => {
  const connectedUser = useConnectedUser()
  const eurCurrency = { enum_label: "€", enum_value: "EUR" }

  return (
    <ul className="grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-4 lg:gap-4">
      {!connectedUser ? (
        <FreePlanCard
          currency={plans[0]?.prices?.[0].price.currency ?? eurCurrency}
        />
      ) : null}
      {plans.map((plan) => {
        return (
          <li key={plan.id} className="grid">
            <PlanCard
              plan={plan}
              onSelect={() => onSelectPlan?.(plan)}
              isSelected={isSelectedPlan?.(plan)}
              color="secondary"
              godfather={godfather}
              busy={isSelectedPlan?.(plan) && status === "loading"}
              disabled={["loading", "success"].includes(status)}
              requireLogin={true}
            />
          </li>
        )
      })}
    </ul>
  )
}

const FreePlanCard = ({ currency }) => {
  const translator = useTranslator()
  const router = useRouter()

  const observerPlan = {
    id: "observer",
    name: translator.trans("club.plan.index.observerPlan.name", null, "pages"),
    description: translator.trans(
      "club.plan.index.observerPlan.description",
      null,
      "pages",
    ),
    prices: [
      {
        price: { currency, amount: 0 },
      },
    ],
  }

  return (
    <PlanCard
      plan={observerPlan}
      color="primary"
      requireLogin={false}
      onSelect={() => router.redirect({ name: "app_user_security_register" })}
    />
  )
}
